export type RelativeDateTexts = {
  overdueDays: string;
  overdueDay: string;
  dueInDays: string;
  dueInDay: string;
  justNow: string;
  minutesAgo: string;
  hourAgo: string;
  hoursAgo: string;
};

const defaultTexts: RelativeDateTexts = {
  overdueDays: 'Overdue by {{count}} days',
  overdueDay: 'Overdue by 1 day',
  dueInDays: 'Due in {{count}} days',
  dueInDay: 'Due in 1 day',
  justNow: 'Just now',
  minutesAgo: '{{count}} minutes ago',
  hourAgo: '1 hour ago',
  hoursAgo: '{{count}} hours ago',
};

const replaceCount = (template: string, count: number): string =>
  template.replace('{{count}}', String(count));

/** Format relative date for overdue alerts. Uses dueDate for "days" and omits time. */
export const formatRelativeDateForAlert = (
  dueDate: string | Date | null | undefined,
  createdAt: string | Date | null | undefined,
  texts: Partial<RelativeDateTexts> = {}
): string => {
  const t = { ...defaultTexts, ...texts };
  const date = dueDate ? new Date(dueDate) : createdAt ? new Date(createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (diffDays > 0) {
    return diffDays === 1 ? t.overdueDay : replaceCount(t.overdueDays, diffDays);
  }
  if (diffDays < 0) {
    const absDays = Math.abs(diffDays);
    return absDays === 1 ? t.dueInDay : replaceCount(t.dueInDays, absDays);
  }

  const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
  if (diffHours === 0) {
    const diffMins = Math.floor(diffMs / (60 * 1000));
    return diffMins <= 1 ? t.justNow : replaceCount(t.minutesAgo, diffMins);
  }
  return diffHours === 1 ? t.hourAgo : replaceCount(t.hoursAgo, diffHours);
};

/** @deprecated Use formatRelativeDateForAlert for alerts. Kept for backward compatibility. */
export const formatRelativeDateAndTime = (
  dueDate: string | Date | null | undefined,
  createdAt: string | Date | null | undefined
): string => {
  const date = dueDate ? new Date(dueDate) : createdAt ? new Date(createdAt) : null;
  if (!date || Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  let relative = '';
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (60 * 1000));
      relative = diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    } else {
      relative = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
  } else if (diffDays === 1) {
    relative = '1 day ago';
  } else {
    relative = `${diffDays} days ago`;
  }

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return relative ? `${relative} • ${timeStr}` : timeStr;
};
